import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { currentFeatures, oids } from "@/configs/Settings";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OpenInNew, Visibility } from "@mui/icons-material";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  getAppointmentReferenceFromQuestionnaireTask,
  isQuestionnaireTask,
  postQuestionnaireAnsweredNotificationTask,
  questionnaireTaskWorkflowService,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import {
  QuestionnaireRespondView,
  useQuestionnaireRespond,
  type QuestionnaireRespondLegacyLocationState,
  type QuestionnaireRespondSubmitSuccessMeta,
} from "src-next/modules/questionnaire-builder";
import type { Patient } from "fhir/r5";
import { PatientQuestionnairesDialog } from "src-next/modules/patient-summary";
import {
  UnderlineTab,
  UnderlineTabs,
} from "src-next/shared/ui/navigation/UnderlineTabs";
import { BundlePatientSummary } from "@/services/fhir";
import { GetConsents } from "@/services/consent";
import { GetTask } from "@/services/fhir/task/GetTask";
import { HTTPResponseStatus } from "@/utils/enum";
import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { ScheduleSliderDotsBalloon } from "@/components/schedule/ScheduleSliderDotsBalloon";
import { DashViewAllButton } from "./DashViewAllButton";
import { useFhirData } from "@/utils/useFhirData";
import { getDependentIdentifier } from "@/utils/patient/getDependentIdentifier";
import {
  dependentPatientReferenceFromRecord,
  fetchDependentPatientSummaryQuestionnaireEntries,
  fetchVerifiedPetQuestionnaireSummaryEntries,
  PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
  patientSummaryIdentifierFromRelatedDependent,
  type ConsentReceivedLite,
} from "@/utils/patient/fetchDependentPatientSummaryQuestionnaireEntries";

const TOUCH_MIN_PX = 44;

/** Quantidade exibida no preview (dashboard) antes de "Ver tudo". */
const QUESTIONNAIRE_PREVIEW_LIMIT = 5;

/**
 * - `patient` (default): portal humano — dependentes exigem consentimento “received” + `purpose`.
 * - `Tutor`: FluxVet — tutor (CPF) responde pelos pets verificados; sem GetConsents nem gate por purpose.
 */
export type PatientQuestionnaireTasksSectionVariant = "patient" | "tutor";

type TasksTabValue = "open" | "completed" | "all";

export interface PatientQuestionnaireTasksSectionProps {
  variant?: PatientQuestionnaireTasksSectionVariant;
  /** Reduz espaçamentos internos no mobile dashboard */
  compact?: boolean;
  /** Compact: só abas Abertos e Respondidos (sem Todos) */
  compactTabsOnly?: boolean;
  /** Oculta botão inferior — o pai coloca "Ver tudo" no header da seção */
  hideVerTudoButton?: boolean;
  /** Controle externo do bottom sheet "Ver tudo" */
  allListOpen?: boolean;
  onAllListOpenChange?: (open: boolean) => void;
  /** Aba inicial (padrão: Abertos) */
  defaultTasksTab?: TasksTabValue;
  /** Modal "Ver tudo": só aba Todos, lista vertical (sem tabs no modal) */
  fullListTodosOnly?: boolean;
  onOpenTasksCountChange?: (count: number) => void;
}

const TASKS_TABS: { value: TasksTabValue; label: string }[] = [
  { value: "open", label: "Abertos" },
  { value: "completed", label: "Respondidos" },
  { value: "all", label: "Todos" },
];

function getPatientDisplayName(patient: Patient | undefined): string {
  const fromText = patient?.name?.[0]?.text?.trim();
  return fromText || "Paciente";
}

/** Corpo do preenchimento dentro de `ScreenMobile` (dashboard mobile). */
function QuestionnaireRespondScreenBody({
  state,
  onClose,
}: {
  state: QuestionnaireRespondLegacyLocationState;
  onClose: () => void;
}) {
  const { userPatient } = useSessionContext();
  const { updateTaskInContext } = useTaskWatchingContext();

  const patientName = getPatientDisplayName(userPatient);

  const respondentContextHint = useMemo(() => {
    if (!state.authorReference?.trim()) return undefined;
    const name = state.subjectPatientDisplayName?.trim();
    return name
      ? `Você está respondendo em nome de ${name}. As respostas serão registradas no prontuário desse dependente.`
      : "Você está respondendo por um dependente vinculado à sua conta. As respostas serão registradas no prontuário dele(a).";
  }, [state.authorReference, state.subjectPatientDisplayName]);

  const onSubmitSuccess = useCallback(
    (_meta: QuestionnaireRespondSubmitSuccessMeta) => {
      void (async () => {
        try {
          await questionnaireTaskWorkflowService.patchQuestionnaireTaskMarkCompleted(
            state.patientTaskId,
          );
          updateTaskInContext(state.patientTaskId, { status: "completed" });
        } catch (error) {
          console.error("QuestionnaireRespondScreenBody patch task", error);
          toast.error(
            "Não foi possível marcar a task do paciente como respondida.",
          );
        }

        try {
          await postQuestionnaireAnsweredNotificationTask({
            patientDisplayName: patientName,
            questionnaireTitle: state.questionnaireTitle,
            questionnaireId: state.questionnaireId,
            subjectReference: state.subjectReference,
            requesterPractitionerRoleReference:
              state.requesterPractitionerRoleReference,
            ownerOrganizationReference: state.ownerOrganizationReference,
          });
        } catch (error) {
          console.error(
            "QuestionnaireRespondScreenBody create notification task",
            error,
          );
          toast.error(
            "Resposta enviada, mas não foi possível notificar a equipe via task.",
          );
        } finally {
          onClose();
        }
      })();
    },
    [onClose, patientName, state, updateTaskInContext],
  );

  const hookResult = useQuestionnaireRespond({
    questionnaireId: state.questionnaireId,
    subjectReference: state.subjectReference,
    ...(state.appointmentReference
      ? { appointmentReference: state.appointmentReference }
      : {}),
    ...(state.authorReference?.trim()
      ? { authorReference: state.authorReference.trim() }
      : {}),
    onSubmitSuccess,
  });

  return (
    <QuestionnaireRespondView
      {...hookResult}
      variant="patientPortal"
      respondentContextHint={respondentContextHint}
    />
  );
}

const QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL =
  "http://fluxmed.com.br/StructureDefinition/questionnaire-usage-type";

/** Metadados comuns às linhas (titular ou dependente consentido). */
type QuestionnaireSubjectRowMeta = {
  /** Patient/... do sujeito clínico (titular ou dependente). */
  subjectPatientReference: string;
  /** Nome do dependente quando a linha não for do titular. */
  subjectPatientDisplayName?: string;
};

type QuestionnaireListItem =
  | ({
      kind: "task";
      key: string;
      title: string;
      dateIso?: string;
      dateLabel?: string;
      questionnaireReference?: string;
      task: fhir5.Task;
    } & QuestionnaireSubjectRowMeta)
  | ({
      kind: "response";
      key: string;
      title: string;
      dateIso?: string;
      dateLabel?: string;
      questionnaireReference?: string;
      questionnaireResponse: fhir5.QuestionnaireResponse;
      /**
       * Se existir uma Task correspondente, mantemos referência para permitir
       * reaproveitar a mesma lógica de "Ver minhas respostas" quando fizer sentido.
       */
      task?: fhir5.Task;
    } & QuestionnaireSubjectRowMeta);

type EnrichedQuestionnaireResponseRow = {
  bundleEntry: any;
  subjectPatientReference: string;
  subjectPatientDisplayName?: string;
};

function normalizeQuestionnaireReference(ref: unknown): string | undefined {
  const raw = typeof ref === "string" ? ref : "";
  const t = raw.trim();
  return t ? t : undefined;
}

function questionnaireReferenceFromTask(task: fhir5.Task): string | undefined {
  return normalizeQuestionnaireReference(task.focus?.reference);
}

function questionnaireReferenceFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const q = questionnaireResponse.questionnaire as unknown;
  if (typeof q === "string") return normalizeQuestionnaireReference(q);
  if (q && typeof q === "object" && "reference" in (q as any)) {
    return normalizeQuestionnaireReference((q as any).reference);
  }
  return undefined;
}

function questionnaireTitleFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string {
  const extensions = questionnaireResponse.extension as unknown;
  if (Array.isArray(extensions)) {
    const ext = extensions.find(
      (e: any) =>
        e?.url ===
        "http://fluxmed.com.br/StructureDefinition/questionnaire-title",
    );
    const t = String((ext as any)?.valueString ?? "").trim();
    if (t) return t;
  }
  const ref = questionnaireReferenceFromResponse(questionnaireResponse);
  return ref ? ref : "Questionário";
}

function dateIsoFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const authored = String(
    (questionnaireResponse as any)?.authored ?? "",
  ).trim();
  if (authored) return authored;
  const lastUpdated = String(
    (questionnaireResponse as any)?.meta?.lastUpdated ?? "",
  ).trim();
  return lastUpdated || undefined;
}

function dateLabelFromIso(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return d.format("DD/MM/YYYY HH:mm");
}

function primaryAuthorReferenceFromResponse(
  questionnaireResponse: fhir5.QuestionnaireResponse,
): string | undefined {
  const a = questionnaireResponse.author as unknown;
  if (!a) return undefined;
  if (Array.isArray(a)) {
    const r = String((a[0] as { reference?: string })?.reference ?? "").trim();
    return r || undefined;
  }
  const r = String((a as { reference?: string })?.reference ?? "").trim();
  return r || undefined;
}

/**
 * Dependente vendo o próprio painel: `subject` sou eu e `author` é outro Patient
 * (responsável que respondeu pelo portal).
 */
function isRespondedByResponsibleForViewer(
  questionnaireResponse: fhir5.QuestionnaireResponse,
  viewerPatientReference: string,
): boolean {
  const subj = String(questionnaireResponse.subject?.reference ?? "").trim();
  const auth = primaryAuthorReferenceFromResponse(questionnaireResponse);
  if (!subj || !auth) return false;
  if (subj !== viewerPatientReference.trim()) return false;
  return auth !== subj;
}

function filterQuestionnaireResponseEntriesWithUsageType(
  bundle: fhir5.Bundle | undefined,
): any[] {
  if (!bundle) return [];
  const questionnaireData = useFhirData.extractPatientSummaryResource(
    bundle,
    "QuestionnaireResponse",
  );
  const questionnaireResponseEntries = questionnaireData?.[0]?.entry ?? [];
  return (
    questionnaireResponseEntries.filter((bundleEntry: any) => {
      const extensions = bundleEntry?.resource?.extension;
      if (!Array.isArray(extensions)) return false;
      return extensions.some(
        (extension: any) =>
          extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
      );
    }) ?? []
  );
}

/**
 * Lista no painel do paciente apenas Tasks de questionário (`code` + `focus`).
 *
 * Estados:
 * - `requested`: aguardando resposta (enviado pelo profissional).
 * - `completed`: Task atualizada após envio do QuestionnaireResponse em
 *   `/questionnaire-respond` (não há marcação manual nesta seção).
 *
 * Botão "Abrir questionário": navega para o fluxo de preenchimento com state
 * necessário para criar o QuestionnaireResponse e concluir a Task.
 *
 * Responsáveis com dependentes verificados + consentimento “received” também veem
 * e respondem questionários dos dependentes (`subject` = dependente; `author` = titular).
 *
 * Modo `Tutor`: mesma UI/ações, porém apenas pets (`patientRelated` verificados), sem consentimento.
 */
export const PatientQuestionnaireTasksSection = memo(
  function PatientQuestionnaireTasksSection({
    variant = "patient",
    compact = false,
    compactTabsOnly = false,
    hideVerTudoButton = false,
    allListOpen: allListOpenProp,
    onAllListOpenChange,
    defaultTasksTab = "open",
    fullListTodosOnly = false,
    onOpenTasksCountChange,
  }: PatientQuestionnaireTasksSectionProps) {
    const theme = useTheme();
    const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
    const isTutorMode = !currentFeatures.defaultApp;
    const { user, access, patientRelated } = useSessionContext();
    const { allTasks } = useTaskWatchingContext();
    const { setIsSidebarExpanded } = useSidebarNavigation();
    const navigate = useNavigate();

    const [tasksTab, setTasksTab] = useState<TasksTabValue>(defaultTasksTab);
    /** Aba ativa dentro do bottom sheet "Ver tudo" (pode incluir "Todos"). */
    const [fullListTasksTab, setFullListTasksTab] =
      useState<TasksTabValue>(defaultTasksTab);
    const [allListOpenInternal, setAllListOpenInternal] = useState(false);
    const allListOpen = allListOpenProp ?? allListOpenInternal;
    const setAllListOpen = onAllListOpenChange ?? setAllListOpenInternal;
    // isCollapsed agora é gerenciado pelo SortableSection pai
    const [questionnairesDialogOpen, setQuestionnairesDialogOpen] =
      useState(false);
    const [initialQuestionnaireReference, setInitialQuestionnaireReference] =
      useState<string | undefined>(undefined);
    const [
      initialQuestionnaireResponseAnchorIso,
      setInitialQuestionnaireResponseAnchorIso,
    ] = useState<string | undefined>(undefined);
    const [viewResponsesLoadingTaskKey, setViewResponsesLoadingTaskKey] =
      useState<string | null>(null);
    const [
      questionnaireResponseEnrichedRows,
      setQuestionnaireResponseEnrichedRows,
    ] = useState<EnrichedQuestionnaireResponseRow[]>([]);
    const [questionnaireResponsesLoading, setQuestionnaireResponsesLoading] =
      useState(false);
    /** Evita reentrância sem colocar loading no deps do useCallback (causava loop na aba Respondidos). */
    const questionnaireResponsesInFlightRef = useRef(false);
    const [receivedConsents, setReceivedConsents] = useState<
      ConsentReceivedLite[]
    >([]);
    const [dependentQuestionnaireTasks, setDependentQuestionnaireTasks] =
      useState<fhir5.Task[]>([]);
    const [dialogSummaryBundle, setDialogSummaryBundle] = useState<
      fhir5.Bundle | undefined
    >(undefined);
    const [questionnaireRespondOpen, setQuestionnaireRespondOpen] =
      useState(false);
    const [questionnaireRespondState, setQuestionnaireRespondState] =
      useState<QuestionnaireRespondLegacyLocationState | null>(null);
    const [responsesDetailTitle, setResponsesDetailTitle] = useState<
      string | null
    >(null);
    const questionnairesEmbeddedBackRef = useRef<(() => void) | null>(null);

    const deferAfterAllListClose = useCallback(
      (action: () => void) => {
        setAllListOpen(false);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(action);
        });
      },
      [setAllListOpen],
    );
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const isSliderScrolling = useRef(false);
    const [selectedCard, setSelectedCard] = useState(0);

    const patientSummaryBundleRef = useRef<fhir5.Bundle | undefined>(undefined);
    /** Cache de bundles por Patient reference (titular + dependentes consentidos). */
    const bundleBySubjectPatientRef = useRef<Map<string, fhir5.Bundle>>(
      new Map(),
    );

    const myPatientReference = useMemo(() => {
      const username = user?.username;
      if (typeof username !== "string" || !/^\d{11}$/.test(username)) {
        return "";
      }
      return `Patient/${oids.cpf}-${username}`;
    }, [user?.username]);

    const verifiedDependents = useMemo(() => {
      return (patientRelated ?? []).filter((el: any) => el?.isVerify);
    }, [patientRelated]);

    useEffect(() => {
      if (isTutorMode) {
        setReceivedConsents([]);
        return;
      }
      async function loadConsents() {
        const username = user?.username;
        if (typeof username !== "string" || !username) {
          setReceivedConsents([]);
          return;
        }
        try {
          const receivedResponse = await GetConsents({
            identifier: username,
            type: "received",
          });
          setReceivedConsents(
            receivedResponse?.data
              ?.filter(
                (el: any) => el?.recurso?.id !== username && el?.recurso?.nome,
              )
              ?.map((el: any) => ({
                id: el?.recurso?.id,
                purpose: el?.contexto?.motivoAcesso,
              })) ?? [],
          );
        } catch (err) {
          console.log("PatientQuestionnaireTasksSection GetConsents", err);
          setReceivedConsents([]);
        }
      }
      if (access?.type === "patient") void loadConsents();
    }, [access?.type, isTutorMode, user?.username]);

    const authorizedDependentPatientReferences = useMemo(() => {
      const refs: string[] = [];
      verifiedDependents.forEach((dep: any) => {
        const depId = getDependentIdentifier(dep);
        if (!depId) return;
        if (!isTutorMode) {
          if (!receivedConsents.find((c) => c?.id === depId)?.purpose) return;
        }
        const pref = dependentPatientReferenceFromRecord(dep, oids.cpf);
        if (pref) refs.push(pref);
      });
      return refs;
    }, [isTutorMode, receivedConsents, verifiedDependents]);

    const dependentDisplayByPatientRef = useMemo(() => {
      const m = new Map<string, string>();
      verifiedDependents.forEach((dep: any) => {
        const pref = dependentPatientReferenceFromRecord(dep, oids.cpf);
        const label = String(dep?.name?.[0]?.text ?? "").trim();
        if (pref && label) m.set(pref.trim(), label);
      });
      return m;
    }, [verifiedDependents]);

    useEffect(() => {
      async function loadDependentTasks() {
        if (!authorizedDependentPatientReferences.length) {
          setDependentQuestionnaireTasks([]);
          return;
        }
        try {
          const bundles = await Promise.all(
            authorizedDependentPatientReferences.map((subjectRef) =>
              GetTask({ subject: subjectRef }),
            ),
          );
          const resources = bundles.flatMap(
            (b: any) => b?.entry?.map((e: any) => e.resource) ?? [],
          );
          const dedup = new Map<string, fhir5.Task>();
          resources.forEach((t: fhir5.Task) => {
            if (!t?.id || !isQuestionnaireTask(t)) return;
            dedup.set(String(t.id), t);
          });
          setDependentQuestionnaireTasks(Array.from(dedup.values()));
        } catch (err) {
          console.log(
            "PatientQuestionnaireTasksSection GetTask dependentes",
            err,
          );
          setDependentQuestionnaireTasks([]);
        }
      }
      if (access?.type === "patient" || isTutorMode) void loadDependentTasks();
    }, [access?.type, authorizedDependentPatientReferences, isTutorMode]);

    const questionnaireTasks = useMemo(() => {
      if (isTutorMode) {
        if (!authorizedDependentPatientReferences.length) return [];
        const allowed = new Set(
          authorizedDependentPatientReferences.map((r) => r.trim()),
        );
        const merged = new Map<string, fhir5.Task>();
        [...allTasks, ...dependentQuestionnaireTasks].forEach(
          (task: fhir5.Task) => {
            if (!isQuestionnaireTask(task)) return;
            const forRef = String(task.for?.reference ?? "").trim();
            if (!forRef || !allowed.has(forRef)) return;
            if (task.id) merged.set(String(task.id), task);
          },
        );
        return Array.from(merged.values()).sort(
          (taskA: fhir5.Task, taskB: fhir5.Task) => {
            const dateA = dayjs(taskA.authoredOn || taskA.meta?.lastUpdated);
            const dateB = dayjs(taskB.authoredOn || taskB.meta?.lastUpdated);
            return dateB.valueOf() - dateA.valueOf();
          },
        );
      }
      if (!myPatientReference) return [];
      const allowed = new Set<string>([
        myPatientReference.trim(),
        ...authorizedDependentPatientReferences.map((r) => r.trim()),
      ]);
      const merged = new Map<string, fhir5.Task>();
      [...allTasks, ...dependentQuestionnaireTasks].forEach(
        (task: fhir5.Task) => {
          if (!isQuestionnaireTask(task)) return;
          const forRef = String(task.for?.reference ?? "").trim();
          if (!forRef || !allowed.has(forRef)) return;
          if (task.id) merged.set(String(task.id), task);
        },
      );
      return Array.from(merged.values()).sort(
        (taskA: fhir5.Task, taskB: fhir5.Task) => {
          const dateA = dayjs(taskA.authoredOn || taskA.meta?.lastUpdated);
          const dateB = dayjs(taskB.authoredOn || taskB.meta?.lastUpdated);
          return dateB.valueOf() - dateA.valueOf();
        },
      );
    }, [
      allTasks,
      authorizedDependentPatientReferences,
      dependentQuestionnaireTasks,
      isTutorMode,
      myPatientReference,
    ]);

    const ensurePatientSummaryBundle = useCallback(async () => {
      if (patientSummaryBundleRef.current) {
        bundleBySubjectPatientRef.current.set(
          myPatientReference.trim(),
          patientSummaryBundleRef.current,
        );
        return patientSummaryBundleRef.current;
      }
      const username = user?.username;
      if (typeof username !== "string" || !username) {
        toast.error("Não foi possível carregar seus questionários.");
        return undefined;
      }
      // MHD: mesmo recorte que `PatientSummary` + `BundlePatientSummary` com `entries`
      // (só QuestionnaireResponse — ver `AuxiliarData.PatientSummary` / filterEntries).
      const bundleResponse = await BundlePatientSummary({
        identifier: username,
        purposeofuse: "Atendimento",
        entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
      });
      if (!bundleResponse) {
        return undefined;
      }
      if (
        typeof bundleResponse === "object" &&
        "status" in bundleResponse &&
        bundleResponse.status === HTTPResponseStatus.Forbidden
      ) {
        navigate("/");
        return undefined;
      }
      const bundle = bundleResponse as fhir5.Bundle;
      patientSummaryBundleRef.current = bundle;
      bundleBySubjectPatientRef.current.set(myPatientReference.trim(), bundle);
      return bundle;
    }, [myPatientReference, navigate, user?.username]);

    /**
     * Resolve o bundle Patient Summary para abrir o diálogo de respostas
     * (titular ou dependente), buscando sob demanda se ainda não estiver em cache.
     */
    const resolveBundleForSubjectPatientReference = useCallback(
      async (subjectPatientRef: string): Promise<fhir5.Bundle | undefined> => {
        const key = subjectPatientRef.trim();
        const cached = bundleBySubjectPatientRef.current.get(key);
        if (cached) return cached;
        if (key === myPatientReference.trim()) {
          return ensurePatientSummaryBundle();
        }
        const dep = verifiedDependents.find(
          (d: any) =>
            dependentPatientReferenceFromRecord(d, oids.cpf)?.trim() === key,
        );
        /** Consent “received” no legado usa o id derivado do dependente (ex. CPF). */
        const dependenteIdForConsent = dep
          ? getDependentIdentifier(dep)
          : undefined;
        if (isTutorMode) {
          const dependenteIdForSummary =
            dep != null
              ? patientSummaryIdentifierFromRelatedDependent(dep)
              : undefined;
          if (!dependenteIdForSummary) {
            toast.error("Não foi possível carregar o prontuário deste pet.");
            return undefined;
          }
          const bundleResponse = await BundlePatientSummary({
            identifier: dependenteIdForSummary,
            purposeofuse: "Atendimento",
            entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
          });
          if (
            typeof bundleResponse === "object" &&
            bundleResponse &&
            "status" in bundleResponse &&
            bundleResponse.status === HTTPResponseStatus.Forbidden
          ) {
            navigate("/");
            return undefined;
          }
          const bundle = bundleResponse as fhir5.Bundle;
          bundleBySubjectPatientRef.current.set(key, bundle);
          return bundle;
        }
        const purpose = dependenteIdForConsent
          ? receivedConsents.find((c) => c?.id === dependenteIdForConsent)
              ?.purpose
          : undefined;
        if (!dependenteIdForConsent || !purpose) {
          toast.error(
            "Não foi possível carregar o prontuário deste dependente.",
          );
          return undefined;
        }
        const bundleResponse = await BundlePatientSummary({
          identifier: dependenteIdForConsent,
          purposeofuse: purpose,
          entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
        });
        if (
          typeof bundleResponse === "object" &&
          bundleResponse &&
          "status" in bundleResponse &&
          bundleResponse.status === HTTPResponseStatus.Forbidden
        ) {
          navigate("/");
          return undefined;
        }
        const bundle = bundleResponse as fhir5.Bundle;
        bundleBySubjectPatientRef.current.set(key, bundle);
        return bundle;
      },
      [
        ensurePatientSummaryBundle,
        isTutorMode,
        myPatientReference,
        navigate,
        receivedConsents,
        verifiedDependents,
      ],
    );

    const ensureQuestionnaireResponsesWithUsageTypeExtension =
      useCallback(async () => {
        if (questionnaireResponsesInFlightRef.current) return;
        questionnaireResponsesInFlightRef.current = true;

        setQuestionnaireResponsesLoading(true);
        try {
          if (isTutorMode) {
            const dependentRows =
              verifiedDependents.length > 0
                ? await fetchVerifiedPetQuestionnaireSummaryEntries(
                    verifiedDependents,
                  )
                : [];

            dependentRows.forEach((row) => {
              bundleBySubjectPatientRef.current.set(
                row.subjectPatientReference.trim(),
                row.bundle,
              );
            });

            const mergedDependentEntries: EnrichedQuestionnaireResponseRow[] =
              dependentRows.flatMap((row) =>
                row.filteredEntries.map((bundleEntry: any) => ({
                  bundleEntry,
                  subjectPatientReference: row.subjectPatientReference.trim(),
                  subjectPatientDisplayName: row.subjectPatientDisplayName,
                })),
              );

            const dedupVet = new Map<
              string,
              EnrichedQuestionnaireResponseRow
            >();
            mergedDependentEntries.forEach((row) => {
              const resource = row.bundleEntry?.resource;
              const id =
                resource?.id != null && String(resource.id).trim()
                  ? String(resource.id).trim()
                  : undefined;
              const dedupKey = id
                ? `${row.subjectPatientReference}|${id}`
                : `${row.subjectPatientReference}|${JSON.stringify(row.bundleEntry)}`;
              dedupVet.set(dedupKey, row);
            });

            setQuestionnaireResponseEnrichedRows(Array.from(dedupVet.values()));
            return;
          }

          const bundle = await ensurePatientSummaryBundle();
          if (!bundle) {
            setQuestionnaireResponseEnrichedRows([]);
            return;
          }

          const titularFiltered =
            filterQuestionnaireResponseEntriesWithUsageType(bundle);
          const titularRows: EnrichedQuestionnaireResponseRow[] =
            titularFiltered.map((bundleEntry: any) => ({
              bundleEntry,
              subjectPatientReference: myPatientReference.trim(),
              subjectPatientDisplayName: undefined,
            }));

          const dependentRows =
            verifiedDependents.length && receivedConsents.length
              ? await fetchDependentPatientSummaryQuestionnaireEntries({
                  dependents: verifiedDependents,
                  receivedConsents,
                })
              : [];

          dependentRows.forEach((row) => {
            bundleBySubjectPatientRef.current.set(
              row.subjectPatientReference.trim(),
              row.bundle,
            );
          });

          const mergedDependentEntries: EnrichedQuestionnaireResponseRow[] =
            dependentRows.flatMap((row) =>
              row.filteredEntries.map((bundleEntry: any) => ({
                bundleEntry,
                subjectPatientReference: row.subjectPatientReference.trim(),
                subjectPatientDisplayName: row.subjectPatientDisplayName,
              })),
            );

          const dedup = new Map<string, EnrichedQuestionnaireResponseRow>();
          [...titularRows, ...mergedDependentEntries].forEach((row) => {
            const resource = row.bundleEntry?.resource;
            const id =
              resource?.id != null && String(resource.id).trim()
                ? String(resource.id).trim()
                : undefined;
            const dedupKey = id
              ? `${row.subjectPatientReference}|${id}`
              : `${row.subjectPatientReference}|${JSON.stringify(row.bundleEntry)}`;
            dedup.set(dedupKey, row);
          });

          setQuestionnaireResponseEnrichedRows(Array.from(dedup.values()));
        } catch (err) {
          console.log("PatientQuestionnaireTasksSection error:", err);
          setQuestionnaireResponseEnrichedRows([]);
        } finally {
          questionnaireResponsesInFlightRef.current = false;
          setQuestionnaireResponsesLoading(false);
        }
      }, [
        ensurePatientSummaryBundle,
        isTutorMode,
        myPatientReference,
        receivedConsents,
        verifiedDependents,
      ]);

    useEffect(() => {
      if (tasksTab === "completed" || tasksTab === "all") {
        void ensureQuestionnaireResponsesWithUsageTypeExtension();
      }
    }, [ensureQuestionnaireResponsesWithUsageTypeExtension, tasksTab]);

    useEffect(() => {
      if (fullListTasksTab === "completed" || fullListTasksTab === "all") {
        void ensureQuestionnaireResponsesWithUsageTypeExtension();
      }
    }, [ensureQuestionnaireResponsesWithUsageTypeExtension, fullListTasksTab]);

    useEffect(() => {
      if (allListOpen && fullListTodosOnly) {
        void ensureQuestionnaireResponsesWithUsageTypeExtension();
      }
    }, [allListOpen, ensureQuestionnaireResponsesWithUsageTypeExtension, fullListTodosOnly]);

    /** Ao abrir "Ver tudo", inicia na mesma aba do preview (exceto modal só Todos). */
    useEffect(() => {
      if (!allListOpen) return;
      if (fullListTodosOnly) {
        setFullListTasksTab("all");
        return;
      }
      setFullListTasksTab(tasksTab);
    }, [allListOpen, fullListTodosOnly, tasksTab]);

    useEffect(() => {
      patientSummaryBundleRef.current = undefined;
      bundleBySubjectPatientRef.current.clear();
      setQuestionnaireResponseEnrichedRows([]);
      setDependentQuestionnaireTasks([]);
      questionnaireResponsesInFlightRef.current = false;
    }, [user?.username]);

    const responseItems = useMemo((): QuestionnaireListItem[] => {
      return (questionnaireResponseEnrichedRows ?? [])
        .map((row: EnrichedQuestionnaireResponseRow, index: number) => {
          const questionnaireResponse = row.bundleEntry?.resource as
            | fhir5.QuestionnaireResponse
            | undefined;
          if (!questionnaireResponse) return null;
          const questionnaireReference = questionnaireReferenceFromResponse(
            questionnaireResponse,
          );
          const dateIso = dateIsoFromResponse(questionnaireResponse);
          const title = questionnaireTitleFromResponse(questionnaireResponse);

          return {
            kind: "response" as const,
            key:
              String(questionnaireResponse.id ?? "").trim() ||
              `${row.subjectPatientReference}|${questionnaireReference ?? index}`,
            title,
            dateIso,
            dateLabel: dateLabelFromIso(dateIso),
            questionnaireReference,
            questionnaireResponse,
            subjectPatientReference: row.subjectPatientReference,
            subjectPatientDisplayName: row.subjectPatientDisplayName,
          };
        })
        .filter(Boolean) as QuestionnaireListItem[];
    }, [questionnaireResponseEnrichedRows]);

    const taskItems = useMemo((): Extract<
      QuestionnaireListItem,
      { kind: "task" }
    >[] => {
      return questionnaireTasks.map((task: fhir5.Task, index) => {
        const questionnaireReference = questionnaireReferenceFromTask(task);
        const dateIsoRaw = String(
          task.authoredOn || task.meta?.lastUpdated || "",
        ).trim();
        const dateIso = dateIsoRaw || undefined;
        const forRef = String(task.for?.reference ?? "").trim();
        const isDependentTask =
          forRef.length > 0 && forRef !== myPatientReference.trim();
        return {
          kind: "task",
          key: task.id?.trim() ?? `patient-q-task-row-${index}`,
          title: String(task.description || "Questionário").trim(),
          dateIso,
          dateLabel: dateLabelFromIso(dateIso),
          questionnaireReference,
          task,
          subjectPatientReference: forRef || myPatientReference.trim(),
          subjectPatientDisplayName: isDependentTask
            ? dependentDisplayByPatientRef.get(forRef)
            : undefined,
        };
      });
    }, [dependentDisplayByPatientRef, myPatientReference, questionnaireTasks]);

    const openTaskItems = useMemo(() => {
      return taskItems.filter((it) => it.task.status === "requested");
    }, [taskItems]);

    // Repassa a contagem ao pai (SectionHeader.badge) quando solicitado.
    useEffect(() => {
      onOpenTasksCountChange?.(openTaskItems.length);
    }, [onOpenTasksCountChange, openTaskItems.length]);

    const allTabItems = useMemo((): QuestionnaireListItem[] => {
      /**
       * Regra de negócio (dashboard):
       * - "Abertos": apenas Tasks requested
       * - "Respondidos": apenas QuestionnaireResponses (com usage-type extension)
       * - "Todos": Abertos (Tasks) + Respondidos (QuestionnaireResponses)
       */
      const merged: QuestionnaireListItem[] = [
        ...openTaskItems,
        ...responseItems,
      ];

      return merged.sort((a, b) => {
        const da = dayjs(a.dateIso || undefined);
        const db = dayjs(b.dateIso || undefined);
        if (da.isValid() && db.isValid()) return db.valueOf() - da.valueOf();
        if (db.isValid()) return 1;
        if (da.isValid()) return -1;
        return 0;
      });
    }, [openTaskItems, responseItems]);

    const sortQuestionnaireItemsByDateDesc = (
      items: QuestionnaireListItem[],
    ) =>
      [...items].sort((a, b) => {
        const da = dayjs(a.dateIso || undefined);
        const db = dayjs(b.dateIso || undefined);
        if (da.isValid() && db.isValid()) return db.valueOf() - da.valueOf();
        if (db.isValid()) return 1;
        if (da.isValid()) return -1;
        return 0;
      });

    const filteredItems = useMemo((): QuestionnaireListItem[] => {
      if (tasksTab === "open") {
        return sortQuestionnaireItemsByDateDesc(openTaskItems);
      }
      if (tasksTab === "completed") {
        return sortQuestionnaireItemsByDateDesc(responseItems);
      }
      return allTabItems;
    }, [allTabItems, openTaskItems, responseItems, tasksTab]);

    const fullListFilteredItems = useMemo((): QuestionnaireListItem[] => {
      if (fullListTasksTab === "open") {
        return sortQuestionnaireItemsByDateDesc(openTaskItems);
      }
      if (fullListTasksTab === "completed") {
        return sortQuestionnaireItemsByDateDesc(responseItems);
      }
      return allTabItems;
    }, [allTabItems, fullListTasksTab, openTaskItems, responseItems]);

    const previewItems = useMemo(
      () => filteredItems.slice(0, QUESTIONNAIRE_PREVIEW_LIMIT),
      [filteredItems],
    );

    /** Web (compact=false): grade com todos os itens; mobile compact: preview + Ver tudo. */
    const gridItems = compact ? previewItems : filteredItems;

    const handleChangeTasksTab = useCallback(
      (_e: React.SyntheticEvent, next: TasksTabValue) => {
        setTasksTab(next);
      },
      [],
    );

    useEffect(() => {
      setSelectedCard(0);
      if (sliderRef.current) sliderRef.current.scrollLeft = 0;
    }, [tasksTab, previewItems.length]);

    const handleSliderScroll = useCallback(() => {
      if (isSliderScrolling.current) return;
      const el = sliderRef.current;
      if (!el || el.clientWidth <= 0) return;
      const nextIndex = Math.round(el.scrollLeft / el.clientWidth);
      if (
        nextIndex !== selectedCard &&
        nextIndex >= 0 &&
        nextIndex < previewItems.length
      ) {
        setSelectedCard(nextIndex);
      }
    }, [previewItems.length, selectedCard]);

    const scrollSliderToIndex = useCallback(
      (index: number) => {
        const el = sliderRef.current;
        if (!el || previewItems.length <= 1) return;
        isSliderScrolling.current = true;
        el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
        window.setTimeout(() => {
          isSliderScrolling.current = false;
        }, 320);
      },
      [previewItems.length],
    );

    const handleOpenQuestionnaire = useCallback(
      (task: fhir5.Task) => {
        const focusReference = task.focus?.reference?.trim() ?? "";
        const questionnaireId = focusReference.startsWith("Questionnaire/")
          ? focusReference.replace("Questionnaire/", "").trim()
          : "";

        const patientTaskId = task.id?.trim() ?? "";
        const subjectFromTask = String(task.for?.reference ?? "").trim();
        const subjectReference = subjectFromTask || myPatientReference.trim();
        const requesterPractitionerRoleReference =
          task.requester?.reference?.trim() ?? "";
        const ownerOrganizationReference = task.owner?.reference?.trim() ?? "";
        const questionnaireTitle = (task.description || "Questionário").trim();
        const appointmentReference =
          getAppointmentReferenceFromQuestionnaireTask(task);

        const isAnsweringForDependent =
          subjectReference.trim() !== myPatientReference.trim();

        if (
          !questionnaireId ||
          !patientTaskId ||
          !subjectReference ||
          !requesterPractitionerRoleReference ||
          !ownerOrganizationReference
        ) {
          toast.error(
            "Não foi possível abrir o questionário (dados da task incompletos).",
          );
          return;
        }

        const respondState: QuestionnaireRespondLegacyLocationState = {
          questionnaireId,
          questionnaireTitle,
          subjectReference,
          patientTaskId,
          requesterPractitionerRoleReference,
          ownerOrganizationReference,
          ...(appointmentReference ? { appointmentReference } : {}),
          ...(isAnsweringForDependent
            ? {
                authorReference: myPatientReference.trim(),
                subjectPatientDisplayName:
                  dependentDisplayByPatientRef.get(subjectReference.trim()) ||
                  undefined,
              }
            : {}),
        };

        setIsSidebarExpanded(false);

        if (isMobileLayout) {
          deferAfterAllListClose(() => {
            setQuestionnaireRespondState(respondState);
            setQuestionnaireRespondOpen(true);
          });
          return;
        }

        navigate("/questionnaire-respond", {
          state: respondState,
        });
      },
      [
        deferAfterAllListClose,
        dependentDisplayByPatientRef,
        isMobileLayout,
        myPatientReference,
        navigate,
        setIsSidebarExpanded,
      ],
    );

    const openQuestionnairesDialogAfterSheetClose = useCallback(
      () => {
        deferAfterAllListClose(() => setQuestionnairesDialogOpen(true));
      },
      [deferAfterAllListClose],
    );

    const handleViewResponses = useCallback(
      async (task: fhir5.Task, taskRowKey: string) => {
        const focusRef = task.focus?.reference?.trim() || undefined;
        const subjectRef =
          String(task.for?.reference ?? "").trim() || myPatientReference;
        setViewResponsesLoadingTaskKey(taskRowKey);
        try {
          const bundle =
            await resolveBundleForSubjectPatientReference(subjectRef);
          if (!bundle) return;
          setDialogSummaryBundle(bundle);
          const metaStr =
            task.meta?.lastUpdated != null
              ? String(task.meta.lastUpdated).trim()
              : "";
          const authoredStr =
            task.authoredOn != null ? String(task.authoredOn).trim() : "";
          const anchorIso = metaStr || authoredStr || undefined;
          setInitialQuestionnaireResponseAnchorIso(anchorIso);
          setInitialQuestionnaireReference(focusRef);
          if (isMobileLayout) {
            openQuestionnairesDialogAfterSheetClose();
          } else {
            setQuestionnairesDialogOpen(true);
          }
        } catch (err) {
          console.log("PatientSummary error:", err);
          toast.error("Não foi possível carregar suas respostas agora.");
        } finally {
          setViewResponsesLoadingTaskKey(null);
        }
      },
      [
        isMobileLayout,
        myPatientReference,
        openQuestionnairesDialogAfterSheetClose,
        resolveBundleForSubjectPatientReference,
      ],
    );

    const handleViewResponsesFromResponse = useCallback(
      async (item: Extract<QuestionnaireListItem, { kind: "response" }>) => {
        setViewResponsesLoadingTaskKey(item.key);
        try {
          const bundle = await resolveBundleForSubjectPatientReference(
            item.subjectPatientReference,
          );
          if (!bundle) return;
          setDialogSummaryBundle(bundle);

          const anchorIso =
            dateIsoFromResponse(item.questionnaireResponse) || undefined;
          setInitialQuestionnaireResponseAnchorIso(anchorIso);

          const questionnaireRef =
            item.questionnaireReference ||
            questionnaireReferenceFromResponse(item.questionnaireResponse);
          setInitialQuestionnaireReference(questionnaireRef);
          if (isMobileLayout) {
            openQuestionnairesDialogAfterSheetClose();
          } else {
            setQuestionnairesDialogOpen(true);
          }
        } catch (err) {
          console.log("PatientSummary error:", err);
          toast.error("Não foi possível carregar suas respostas agora.");
        } finally {
          setViewResponsesLoadingTaskKey(null);
        }
      },
      [isMobileLayout, openQuestionnairesDialogAfterSheetClose, resolveBundleForSubjectPatientReference],
    );

    const handleCloseQuestionnairesDialog = useCallback(() => {
      setQuestionnairesDialogOpen(false);
      setInitialQuestionnaireReference(undefined);
      setInitialQuestionnaireResponseAnchorIso(undefined);
      setDialogSummaryBundle(undefined);
      setResponsesDetailTitle(null);
      questionnairesEmbeddedBackRef.current = null;
    }, []);

    const handleCloseQuestionnaireRespond = useCallback(() => {
      setQuestionnaireRespondOpen(false);
      setQuestionnaireRespondState(null);
    }, []);

    const showQuestionnaireSection = access?.type === "patient" || isTutorMode;

    if (!showQuestionnaireSection) {
      return null;
    }

    if (!myPatientReference) {
      return null;
    }

    const emptyMessage =
      tasksTab === "open"
        ? "Nenhum questionário pendente."
        : tasksTab === "completed"
          ? "Nenhum questionário respondido ainda."
          : "Nenhum questionário encontrado.";

    const fullListEmptyMessage =
      fullListTasksTab === "open"
        ? "Nenhum questionário pendente."
        : fullListTasksTab === "completed"
          ? "Nenhum questionário respondido ainda."
          : "Nenhum questionário encontrado.";

    const renderQuestionnaireCard = (
      item: QuestionnaireListItem,
      index: number,
      options?: { inSlider?: boolean },
    ) => {
      const isTask = item.kind === "task";
      const task = isTask ? item.task : item.task;
      const isPending =
        isTask && (task?.status === "requested" || !task?.status);
      const isDone = isTask && task?.status === "completed";
      const isResponse = item.kind === "response";

      const authoredLabel =
        item.dateLabel ||
        (isTask && task?.authoredOn
          ? dayjs(task.authoredOn).format("DD/MM/YYYY HH:mm")
          : "");

      const showOpenButton =
        isTask &&
        (tasksTab === "open" || (tasksTab === "all" && isPending));
      const showViewButton =
        isResponse ||
        (isTask &&
          (tasksTab === "completed" || (tasksTab === "all" && isDone)));

      const rowKey = item.key || `patient-q-row-${index}`;
      const isThisViewLoading = viewResponsesLoadingTaskKey === rowKey;

      const isDependentRow =
        item.subjectPatientReference.trim() !== myPatientReference.trim();
      const dependentChipLabel = item.subjectPatientDisplayName
        ? `${isTutorMode ? "Pet" : "Dependente"}: ${item.subjectPatientDisplayName}`
        : isTutorMode
          ? "Pet"
          : "Dependente";

      const showProxyAnsweredChip =
        isResponse &&
        isRespondedByResponsibleForViewer(
          item.questionnaireResponse,
          myPatientReference.trim(),
        );

      return (
        <Box
          key={rowKey}
          onClick={
            options?.inSlider ? () => setSelectedCard(index) : undefined
          }
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: compact ? 1.5 : 2,
            border: "1px solid",
            borderLeftWidth: isPending ? 4 : 1,
            borderColor: isPending ? "warning.main" : "divider",
            overflow: "hidden",
            bgcolor: isPending
              ? (t) => `${t.palette.warning.main}08`
              : "background.paper",
            minHeight: compact ? 82 : undefined,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              px: compact ? 1 : 1.25,
              pt: compact ? 0.75 : 2,
              pb: compact ? 0.5 : 1.5,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                fontSize: compact ? "0.78rem" : "0.875rem",
                mb: 0.25,
                lineHeight: 1.25,
              }}
            >
              {item.title || "Questionário"}
            </Typography>

            {authoredLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: compact ? "0.62rem" : undefined }}
              >
                Enviado em {authoredLabel}
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: compact ? 0.5 : 0.75 }}
            >
              <Chip
                size="small"
                label={
                  isResponse
                    ? "Respondido"
                    : isPending
                      ? "Pendente"
                      : "Respondido"
                }
                color={
                  isResponse ? "success" : isPending ? "warning" : "success"
                }
                variant={isPending ? "filled" : "outlined"}
                sx={{
                  height: compact ? 18 : 20,
                  fontSize: compact ? "0.56rem" : "0.68rem",
                  fontWeight: 700,
                }}
              />
              {isDependentRow && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={dependentChipLabel}
                  sx={{ height: compact ? 18 : 20, fontSize: "0.68rem" }}
                />
              )}
              {showProxyAnsweredChip && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  label="Respondido por responsável"
                  sx={{ height: compact ? 18 : 20, fontSize: "0.68rem" }}
                />
              )}
            </Stack>
          </Box>

          {(showOpenButton || showViewButton) && (
            <Box
              sx={{
                px: compact ? 1 : 1.25,
                pb: compact ? 0.75 : 2,
                pt: compact ? 0.25 : 0.5,
              }}
            >
              {showOpenButton && (
                <Button
                  variant="contained"
                  size={compact ? "small" : "medium"}
                  startIcon={<OpenInNew sx={{ fontSize: compact ? 16 : 20 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isTask) handleOpenQuestionnaire(item.task);
                  }}
                  disabled={!isPending || !item.task?.id}
                  fullWidth
                  sx={{
                    minHeight: compact ? 34 : TOUCH_MIN_PX,
                    fontWeight: 700,
                    borderRadius: 2,
                    fontSize: compact ? "0.72rem" : undefined,
                    textTransform: "none",
                  }}
                >
                  Preencher
                </Button>
              )}
              {showViewButton && (
                <Button
                  variant="outlined"
                  size={compact ? "small" : "medium"}
                  startIcon={
                    isThisViewLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Visibility sx={{ fontSize: compact ? 16 : 20 }} />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.kind === "response") {
                      void handleViewResponsesFromResponse(item);
                      return;
                    }
                    void handleViewResponses(item.task, rowKey);
                  }}
                  disabled={
                    viewResponsesLoadingTaskKey !== null ||
                    questionnaireResponsesLoading
                  }
                  fullWidth
                  sx={{
                    minHeight: compact ? 34 : TOUCH_MIN_PX,
                    borderRadius: 2,
                    fontSize: compact ? "0.72rem" : undefined,
                    textTransform: "none",
                    mt: showOpenButton ? 0.5 : 0,
                  }}
                >
                  Ver respostas
                </Button>
              )}
            </Box>
          )}
        </Box>
      );
    };

    const activeFullListTabLabel =
      TASKS_TABS.find((t) => t.value === fullListTasksTab)?.label ??
      "Questionários";

    const visibleTabs = compactTabsOnly
      ? TASKS_TABS.filter((t) => t.value !== "all")
      : TASKS_TABS;

    /** Itens exibidos no modal "Ver tudo". */
    const fullListItems = fullListTodosOnly
      ? allTabItems
      : fullListFilteredItems;

    const fullListModalTitle = fullListTodosOnly
      ? "Questionários — Todos"
      : `Questionários — ${activeFullListTabLabel}`;

    const verTudoButton =
      !hideVerTudoButton &&
      compact &&
      isMobileLayout &&
      filteredItems.length > 0 ? (
        <DashViewAllButton
          onClick={() => {
            if (fullListTodosOnly) setFullListTasksTab("all");
            setAllListOpen(true);
          }}
          count={
            (fullListTodosOnly ? allTabItems.length : filteredItems.length) >
            QUESTIONNAIRE_PREVIEW_LIMIT
              ? fullListTodosOnly
                ? allTabItems.length
                : filteredItems.length
              : undefined
          }
        />
      ) : null;

    return (
      <>
        {/* O Card/SectionHeader são fornecidos pelo SortableSection pai */}
        <>
          {/* Chip local só se o pai não exibe no SectionHeader */}
          {!compact &&
            !onOpenTasksCountChange &&
            openTaskItems.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-start" }}>
                <Chip
                  label={`${openTaskItems.length} pendente${openTaskItems.length > 1 ? "s" : ""}`}
                  size="small"
                  color="warning"
                  sx={{
                    fontWeight: 700,
                    height: 22,
                    fontSize: "0.7rem",
                  }}
                />
              </Box>
            )}

          {/* Tabs com linha verde inferior (UnderlineTabs compartilhado) */}
          <UnderlineTabs
            value={tasksTab}
            onChange={handleChangeTasksTab}
            variant="fullWidth"
            aria-label="Filtro de questionários"
            sx={{
              mb: compact ? 1 : 2,
              ...(compact
                ? {
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      fontSize: "0.75rem",
                    },
                  }
                : {}),
            }}
          >
            {visibleTabs.map((t) => (
              <UnderlineTab key={t.value} value={t.value} label={t.label} />
            ))}
          </UnderlineTabs>

          {questionnaireResponsesLoading && tasksTab !== "open" ? (
            <Stack alignItems="center" sx={{ py: compact ? 1.5 : 3 }}>
              <CircularProgress size={compact ? 28 : 40} />
            </Stack>
          ) : filteredItems.length === 0 ? (
            compact ? (
              <Box
                sx={{
                  width: "100%",
                  py: 0.75,
                  px: 1.25,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{
                    display: "block",
                    fontSize: "0.6875rem",
                    lineHeight: 1.35,
                    fontWeight: 500,
                  }}
                >
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <Typography variant="body2" color="text.disabled" align="center">
                {emptyMessage}
              </Typography>
            </Stack>
            )
          ) : compact ? (
            <>
              <Box
                ref={sliderRef}
                onScroll={handleSliderScroll}
                sx={{
                  display: "flex",
                  width: "100%",
                  overflowX: previewItems.length > 1 ? "auto" : "hidden",
                  overflowY: "hidden",
                  scrollSnapType:
                    previewItems.length > 1 ? "x mandatory" : "none",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {previewItems.map((item, index) => (
                  <Box
                    key={item.key || `patient-q-slide-${index}`}
                    sx={{
                      flex: "0 0 100%",
                      width: "100%",
                      maxWidth: "100%",
                      scrollSnapAlign: "start",
                      scrollSnapStop: "always",
                      boxSizing: "border-box",
                    }}
                  >
                    {renderQuestionnaireCard(item, index, { inSlider: true })}
                  </Box>
                ))}
              </Box>

              {previewItems.length > 1 && (
                <Box sx={{ mt: 0.75 }}>
                  <ScheduleSliderDotsBalloon
                    total={previewItems.length}
                    activeIndex={selectedCard}
                    onDotClick={(index) => {
                      setSelectedCard(index);
                      scrollSliderToIndex(index);
                    }}
                    compact
                  />
                </Box>
              )}

              {verTudoButton}
            </>
          ) : (
            <>
              <Grid container spacing={1.5}>
                {gridItems.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={item.key || index}>
                    {renderQuestionnaireCard(item, index)}
                  </Grid>
                ))}
              </Grid>

              {verTudoButton}
            </>
          )}
        </>

        <MobileBottomSheet
          open={
            allListOpen &&
            !questionnaireRespondOpen &&
            !questionnairesDialogOpen
          }
          onClose={() => setAllListOpen(false)}
          title={fullListModalTitle}
          size="large"
        >
          <Box sx={{ px: 2, pb: 1 }}>
            {!fullListTodosOnly && (
              <UnderlineTabs
                value={fullListTasksTab}
                onChange={(_, next: TasksTabValue) =>
                  setFullListTasksTab(next)
                }
                variant="fullWidth"
                aria-label="Filtro de questionários na lista completa"
                sx={{ mb: 1.5 }}
              >
                {TASKS_TABS.map((t) => (
                  <UnderlineTab
                    key={t.value}
                    value={t.value}
                    label={t.label}
                  />
                ))}
              </UnderlineTabs>
            )}

            {questionnaireResponsesLoading &&
            (fullListTodosOnly || fullListTasksTab !== "open") ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={40} />
              </Stack>
            ) : fullListItems.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <Typography
                  variant="body2"
                  color="text.disabled"
                  align="center"
                >
                  {fullListTodosOnly
                    ? "Nenhum questionário encontrado."
                    : fullListEmptyMessage}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {fullListItems.map((item, index) =>
                  renderQuestionnaireCard(item, index),
                )}
              </Stack>
            )}
          </Box>
        </MobileBottomSheet>

        {isMobileLayout && questionnaireRespondOpen && questionnaireRespondState ? (
          <ScreenMobile
            open={questionnaireRespondOpen}
            onClose={handleCloseQuestionnaireRespond}
            title={questionnaireRespondState.questionnaireTitle}
          >
            <QuestionnaireRespondScreenBody
              state={questionnaireRespondState}
              onClose={handleCloseQuestionnaireRespond}
            />
          </ScreenMobile>
        ) : null}

        {isMobileLayout && questionnairesDialogOpen ? (
          <ScreenMobile
            open={questionnairesDialogOpen}
            onClose={handleCloseQuestionnairesDialog}
            onBack={
              responsesDetailTitle
                ? () => questionnairesEmbeddedBackRef.current?.()
                : undefined
            }
            title={responsesDetailTitle ?? "Respostas do questionário"}
          >
            <PatientQuestionnairesDialog
              embedded
              open={questionnairesDialogOpen}
              onClose={handleCloseQuestionnairesDialog}
              summaryData={
                dialogSummaryBundle ?? patientSummaryBundleRef.current
              }
              initialQuestionnaireReference={initialQuestionnaireReference}
              initialQuestionnaireResponseAnchorIso={
                initialQuestionnaireResponseAnchorIso
              }
              onDetailTitleChange={setResponsesDetailTitle}
              embeddedBackRef={questionnairesEmbeddedBackRef}
            />
          </ScreenMobile>
        ) : questionnairesDialogOpen ? (
          <PatientQuestionnairesDialog
            open={questionnairesDialogOpen}
            onClose={handleCloseQuestionnairesDialog}
            summaryData={dialogSummaryBundle ?? patientSummaryBundleRef.current}
            initialQuestionnaireReference={initialQuestionnaireReference}
            initialQuestionnaireResponseAnchorIso={
              initialQuestionnaireResponseAnchorIso
            }
          />
        ) : null}
      </>
    );
  },
);
